// Spec: .aidev/tasks/11083-preloader/docs/functional_requirements.md §Key decisions
//
// The bead field. Owns geometry, the camera, growth and drawing; knows nothing
// about the page it sits on. The caller supplies a canvas, a palette and a seed,
// and drives it with start()/stop().

import { BUILDERS, MOTION, FORMS } from './forms'

const TAU = 6.283185307

const DEFAULTS = {
  width: 800,
  height: 350,
  beads: 760,
  size: 5,
  spacing: 1.25,
  outline: 1, // fraction of a bead's diameter
  outlineAlpha: 0.2,
  depthFalloff: 0.7,
  hold: 4,
  collapse: 0.9,
  expand: 1.3,
  core: 26,
  idleSpin: (5 * Math.PI) / 180,
  focal: 1400,
  restPitch: 0.42,
  openingPopulation: 40,
  population: null, // a field held short of its full complement; null means grow to `beads`
  onlyForm: null, // build just this form index; null builds the whole set so the cycle can run
  openingWindow: 4,
  arrivalRate: 22,
  hintDelay: 8, // ~12s from load: short reports never get taught a gesture they cannot use
  hintGap: 6,
  reducedMotion: false
}

const FIT_HALF_W = 320
const FIT_HALF_H = 152
const BINS = 192
const PALETTE_FADE = 0.9 // seconds a theme change takes to cross over

const HINT_IN = 0.35
const HINT_GRAB = 0.25
const HINT_A = 1.15
const HINT_B = 1.15
const HINT_REL = 0.25
const HINT_OUT = 0.5
const HINT_LEN = HINT_IN + HINT_GRAB + HINT_A + HINT_B + HINT_REL + HINT_OUT
const HINT_X = 96
const HINT_ARC_OUT = 34
const HINT_ARC_BACK = 26

// Lucide `hand`, used as path data so the cursor stays line art at any scale.
const HAND = [
  'M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2',
  'M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2',
  'M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8',
  'M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15'
]

// `>>> 0` and `| 0` here are unsigned/int32 coercion, which is what the hash and
// the generator are defined in terms of. Math.trunc is neither, and reassigning
// the parameter is the generator's state.
/* eslint-disable unicorn/prefer-math-trunc, no-param-reassign */
function hashSeed(str) {
  let h = 2166136261 >>> 0
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function mulberry32(a) {
  return function random() {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/* eslint-enable unicorn/prefer-math-trunc, no-param-reassign */

const clamp01 = v => (v < 0 ? 0 : v > 1 ? 1 : v)
const easeInCubic = t => t * t * t
const easeOutCubic = t => 1 - Math.pow(1 - t, 3)

// The report failed, so the field lies down. The reader can pull a shape out of the line and
// gravity takes it straight back: the insight is what fell over, the data is still there.
const ERR_LIFT = 0.56 // seconds the line takes to rise into a form
const ERR_G = 2600 // px/s^2 -- a full-height drop reads at about a second
const ERR_REST = 0.42 // restitution; above ~0.6 it reads as rubber rather than data
const ERR_DROP = 0.22 // the line sits this far below centre, as a fraction of height
const ERR_SPAN = 0.86 // and occupies this fraction of the width
const ERR_BOB_GAP = 1.7 // seconds between one bead stirring and the next
const ERR_BOB_LEN = 1.15 // how long a single stir lasts
const ERR_BOB_RISE = 7 // px a middling stir lifts at its top
// Stirs are not all the same size. Squared, so most are slight and the occasional one is a real
// lift -- an even spread reads as a mechanism, which is the opposite of what the line is saying.
const ERR_BOB_LOW = 0.35
const ERR_BOB_HIGH = 1.75
// The line is a plane seen edge-on: only its near edge is drawn at rest, and the rest of the
// field is behind it, arriving as the shape turns out of the edge.
const ERR_EDGE_EVERY = 6 // one bead in this many stands on the visible edge
// The stir answers a cursor crossing the line: sooner, taller, and about where the pointer is.
const ERR_HOVER_GAP = 0.32 // the wait between stirs, as a fraction of the resting one
const ERR_HOVER_RISE = 2.6 // and how much taller they are
// A disturbed bead flicks rather than swells: on its own a taller stir lingers, but one that was
// startled goes up and comes back quickly, which is what separates a reaction from a drift.
const ERR_HOVER_QUICK = 0.25
// The cursor also carries a crest with it: every bead within reach lifts, highest under the
// pointer and easing to nothing at the edge, so crossing the line raises a wave rather than
// picking out single beads.
const ERR_WAVE_REACH = 0.13 // the share of the line the crest spans, either side of the pointer
const ERR_WAVE_RISE = 15 // px at the pointer itself
const ERR_WAVE_EASE = 7 // how quickly the crest arrives and leaves, per second
const ERR_HOVER_REACH = 0.09 // the share of the line a cursor stirs, either side of itself

// Height above the line at `t`, as real bouncing rather than a decaying sine: each arc's apex is
// e^2 of the one before, so a bead that fell further is still moving once the short ones have died.
const errBounce = (h, t) => {
  if (!(h > 0.5)) return 0 // also catches NaN, which the reversed test would let through
  const first = Math.sqrt((2 * h) / ERR_G)
  if (t < first) return h - 0.5 * ERR_G * t * t
  let rem = t - first
  let apex = h * ERR_REST * ERR_REST
  let guard = 0
  while (apex > 0.4 && guard++ < 60) {
    const span = 2 * Math.sqrt((2 * apex) / ERR_G)
    if (rem < span) {
      const mid = rem - span / 2
      return apex - 0.5 * ERR_G * mid * mid
    }
    rem -= span
    apex *= ERR_REST * ERR_REST
  }
  return 0
}

const errOnEdge = i => 0 === i % ERR_EDGE_EVERY

const errSettle = h => {
  if (!(h > 0.5)) return 0
  let t = Math.sqrt((2 * h) / ERR_G)
  let apex = h * ERR_REST * ERR_REST
  let guard = 0
  while (apex > 0.4 && guard++ < 60) {
    t += 2 * Math.sqrt((2 * apex) / ERR_G)
    apex *= ERR_REST * ERR_REST
  }
  return t
}
const easeInOutSine = t => 0.5 - 0.5 * Math.cos(Math.PI * t)

export default class PreloaderEngine {
  constructor(canvas, options = {}) {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')
    this.o = { ...DEFAULTS, ...options }
    this.palette = options.palette || [[34, 61, 100]]
    this.paletteOld = null
    this.paletteMix = 1
    this._cA = [0, 0, 0]
    this._cB = [0, 0, 0]

    this.hand = HAND.map(d => new Path2D(d))

    this.yaw = 0
    this.pitch = this.o.restPitch
    this.yawV = 0
    this.pitchV = 0
    this.dragging = false
    this.dragDist = 0
    this.hasDragged = false
    this.err = null
    this.hoverAt = null
    this.hoverX = 0.5
    this.hoverK = 0
    this.hintT = Infinity
    this.hintPending = true
    this.hintShown = 0

    this.prevIdx = -1
    this.fromIdx = 0
    this.toIdx = 1
    this.clock = 0
    this.phase = 'hold'
    this.live = 0

    this.buf = []
    this.sorted = []
    this.bins = new Int32Array(BINS)
    this.binOf = null
    this.mo = [0, 0, 0]

    this.raf = null
    this.last = 0

    this._resize()
    this.build(options.seed || 'aytm')
    this._bindPointer()
  }

  // ---- setup ---------------------------------------------------------------

  _resize() {
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    this.canvas.width = this.o.width * dpr
    this.canvas.height = this.o.height * dpr
    this.canvas.style.width = `${this.o.width}px`
    this.canvas.style.height = `${this.o.height}px`
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  build(seed) {
    const n = this.o.beads
    const base = hashSeed(String(seed))
    // A field held on one form never draws the other fifteen, and building them all costs about a
    // tenth of a second per field. A report can carry a dozen of these at once.
    const only = this.o.onlyForm

    this.forms = FORMS.map((f, i) => {
      if (null != only && i !== only) return null
      const built = BUILDERS[f.key](n, mulberry32(base + i * 7919))
      return this._measure(this._sortByAngle(built, n), n)
    })

    const rng = mulberry32(base ^ 0x9e3779b9)
    const bead = {
      size: new Float32Array(n),
      stagger: new Float32Array(n),
      cx: new Float32Array(n),
      cy: new Float32Array(n),
      cz: new Float32Array(n),
      rank: new Int32Array(n)
    }
    for (let i = 0; i < n; i++) {
      bead.size[i] = 1 + (rng() - 0.5) * 0.55
      bead.stagger[i] = rng() * 0.3
      const th = rng() * TAU
      const ph = Math.acos(2 * rng() - 1)
      const rr = Math.pow(rng(), 0.4)
      bead.cx[i] = Math.sin(ph) * Math.cos(th) * rr
      bead.cy[i] = Math.sin(ph) * Math.sin(th) * rr
      bead.cz[i] = Math.cos(ph) * rr
      bead.rank[i] = i
    }
    // Arrival order is shuffled: bead index tracks position within a form, so
    // revealing a prefix would light up a wedge instead of a sparser whole.
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1))
      const t = bead.rank[i]
      bead.rank[i] = bead.rank[j]
      bead.rank[j] = t
    }
    this.bead = bead
    this.live = 0

    // The order of the forms is drawn fresh for every seed, so two loads of the
    // same report do not play the same sequence.
    this.rngSeq = mulberry32(base ^ 0x51ed270b)
    this.queue = []
    this.fromIdx = this._nextForm()
    this.toIdx = this._nextForm()
  }

  // One pass over every form, in a fresh order, alternating the two families so
  // no two forms of a kind ever meet. A form cannot come round again until the
  // rest have had their turn; the seam between passes keeps the alternation.
  _refill(afterIdx) {
    const kinds = ['order', 'chaos']
    const want = FORMS[afterIdx].kind === kinds[0] ? 1 : 0
    const fam = [[], []]
    for (let i = 0; i < FORMS.length; i++) fam[kinds.indexOf(FORMS[i].kind)].push(i)
    for (const list of fam)
      for (let i = list.length - 1; i > 0; i--) {
        const j = Math.floor(this.rngSeq() * (i + 1))
        const t = list[i]
        list[i] = list[j]
        list[j] = t
      }
    const out = []
    for (let i = 0; i < Math.max(fam[0].length, fam[1].length); i++) {
      if (i < fam[want].length) out.push(fam[want][i])
      if (i < fam[1 - want].length) out.push(fam[1 - want][i])
    }
    // A new pass opens next to the tail of the old one, and a form two places apart reads as a
    // repeat. Two pairs can collide across that seam: the form now showing against out[1], and the
    // one before it against out[0]. Each is displaced within its own family, which keeps the
    // alternation.
    const swap = (a, b) => {
      const t = out[a]
      out[a] = out[b]
      out[b] = t
    }
    if (out.length > 3) {
      if (out[0] === this.prevIdx) swap(0, 2)
      if (out[1] === afterIdx) swap(1, 3)
    }
    this.queue = out
  }

  // A form may ask for fatter beads: an emblem has to read as solid where a scatter would do, and
  // the field's count is fixed. Eased across the transition so the change arrives with the shape.
  _beadScale() {
    const from = FORMS[this.fromIdx].bead || 1
    if (this.phase !== 'travel') return from
    const to = FORMS[this.toIdx].bead || 1
    return from + (to - from) * clamp01(this.clock / (this.o.collapse + this.o.expand))
  }

  _nextForm() {
    if (!this.queue.length) this._refill(this.toIdx)
    return this.queue.shift()
  }

  _sortByAngle(f, n) {
    const order = Array.from({ length: n }, (_, i) => i)
    order.sort((a, b) => Math.atan2(f.p[a * 3 + 1], f.p[a * 3]) - Math.atan2(f.p[b * 3 + 1], f.p[b * 3]))
    const p = new Float32Array(n * 3)
    const m = new Float32Array(n * 2)
    for (let i = 0; i < n; i++) {
      const j = order[i]
      p[i * 3] = f.p[j * 3]
      p[i * 3 + 1] = f.p[j * 3 + 1]
      p[i * 3 + 2] = f.p[j * 3 + 2]
      m[i * 2] = f.m[j * 2]
      m[i * 2 + 1] = f.m[j * 2 + 1]
    }
    return { p, m }
  }

  _measure(f, n) {
    let minX = 1e9
    let maxX = -1e9
    let minY = 1e9
    let maxY = -1e9
    let minZ = 1e9
    let maxZ = -1e9
    for (let i = 0; i < n; i++) {
      const x = f.p[i * 3]
      const y = f.p[i * 3 + 1]
      const z = f.p[i * 3 + 2]
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
      if (z < minZ) minZ = z
      if (z > maxZ) maxZ = z
    }
    const hw = (maxX - minX) / 2 || 1
    const hh = (maxY - minY) / 2 || 1
    f.k = Math.max(0.45, Math.min(1.45, Math.min(FIT_HALF_W / hw, FIT_HALF_H / hh)))
    f.cx = (minX + maxX) / 2
    f.cy = (minY + maxY) / 2
    f.cz = (minZ + maxZ) / 2

    // Colour bands come from the form's own height, so a form reads as a
    // graduated object rather than confetti.
    const span = maxY - minY || 1
    f.band = new Uint8Array(n)
    for (let i = 0; i < n; i++) {
      const t = (maxY - f.p[i * 3 + 1]) / span
      f.band[i] = 4 - Math.min(4, Math.floor(t * 5))
    }
    return f
  }

  // A palette change crosses over rather than switching: the reader is looking straight at the
  // field when they pick a theme, and an instant swap reads as a glitch.
  setPalette(palette) {
    const next = palette && palette.length ? palette : [[34, 61, 100]]
    if (
      this.palette &&
      next.length === this.palette.length &&
      next.every((c, i) => c.join() === this.palette[i].join())
    )
      return
    this.paletteOld = this._shownPalette()
    this.palette = next
    this.paletteMix = 0
  }

  // What is on screen right now, which mid-cross-over is neither palette but a blend of the two.
  // Starting a second change from the target instead would snap the field before it moved.
  _shownPalette() {
    const cur = this.palette
    const prev = this.paletteOld
    if (!prev || this.paletteMix >= 1) return cur
    const t = this.paletteMix
    return cur.map((c, i) => {
      const p = prev[i % prev.length]
      return [p[0] + (c[0] - p[0]) * t, p[1] + (c[1] - p[1]) * t, p[2] + (c[2] - p[2]) * t]
    })
  }

  // ---- interaction ---------------------------------------------------------

  _bindPointer() {
    // the constructor binds, and start() binds again on every resume; without this the first set
    // is unreachable by _unbindPointer and keeps driving a stopped field
    if (this._bound) return
    this._bound = true
    const c = this.canvas
    this._onDown = e => {
      this.dragging = true
      if (this.err) this._errLift()
      c.classList.add('is-dragging')
      try {
        c.setPointerCapture(e.pointerId)
      } catch {
        /* capture unavailable; dragging still works */
      }
      this.lastPX = e.clientX
      this.lastPY = e.clientY
      this.yawV = 0
      this.pitchV = 0
      this.dragDist = 0
    }
    this._onMove = e => {
      if (this.err) {
        const box = c.getBoundingClientRect()
        this.hoverAt = box.width ? (e.clientX - box.left) / box.width : null
        if (null !== this.hoverAt) this.hoverX = this.hoverAt
      }
      if (!this.dragging) return
      const dx = e.clientX - this.lastPX
      const dy = e.clientY - this.lastPY
      this.lastPX = e.clientX
      this.lastPY = e.clientY
      // A stray click is not a drag; retiring the hint on pointer-down removes
      // the only clue the object can be turned.
      this.dragDist += Math.abs(dx) + Math.abs(dy)
      if (this.dragDist > 10) {
        this.hasDragged = true
        this.hintPending = false
        this.hintT = Infinity
      }
      this.yaw += dx * 0.008
      this.pitch = Math.max(-1.2, Math.min(1.2, this.pitch + dy * 0.006))
      this.yawV = dx * 0.3
      this.pitchV = dy * 0.2
    }
    this._onUp = e => {
      if (!this.dragging) return
      this.dragging = false
      if (this.err) this._errRelease()
      c.classList.remove('is-dragging')
      try {
        c.releasePointerCapture(e.pointerId)
      } catch {
        /* pointer already released */
      }
    }
    this._onLeave = () => {
      this.hoverAt = null
    }
    c.addEventListener('pointerleave', this._onLeave)
    c.addEventListener('pointerdown', this._onDown)
    c.addEventListener('pointermove', this._onMove)
    c.addEventListener('pointerup', this._onUp)
    c.addEventListener('pointercancel', this._onUp)
  }

  _unbindPointer() {
    this._bound = false
    const c = this.canvas
    c.removeEventListener('pointerleave', this._onLeave)
    c.removeEventListener('pointerdown', this._onDown)
    c.removeEventListener('pointermove', this._onMove)
    c.removeEventListener('pointerup', this._onUp)
    c.removeEventListener('pointercancel', this._onUp)
  }

  // ---- growth --------------------------------------------------------------

  /** Add beads because the report delivered more material. */
  addBeads(count) {
    this.live = Math.min(this._population(), this.live + count)
  }

  // ---- timeline ------------------------------------------------------------

  // How many beads this field is allowed to reach. A field held below its full
  // population must still be stepped -- the step is also what advances the colour
  // cross-over, so a field held by not stepping it can never follow the palette.
  _population() {
    const target = this.o.population
    return null == target ? this.o.beads : Math.max(0, Math.min(this.o.beads, target))
  }

  /**
   * Lay the field down as a dead line, or hand it back to the running cycle. In this state the
   * form never advances on its own -- the reader lifts one out and gravity returns it.
   */
  setError(on) {
    if (!on) {
      this.err = null
      // a lift left both indices on one form; without this the first transition back is a
      // collapse and re-expansion into the same shape
      if (this.fromIdx === this.toIdx) this.toIdx = this._nextForm()
      return
    }
    // The resting line reads the cycle's own squeeze and bead scale, so an error arriving mid
    // transition would freeze the field at whatever size that moment held.
    this.phase = 'hold'
    this.clock = 0
    // A lesson frozen mid-arc resumes by assigning a yaw captured before the pause, discarding
    // everything the idle spin added; retire it instead and let it re-arm on the way out.
    this.hintT = Infinity
    this.hintPending = true
    this.canvas.classList.remove('is-dragging')
    this.err = {
      state: 'line',
      t: 0,
      fx: null,
      fy: null,
      dur: null,
      bobs: [],
      nextBob: 0,
      rng: mulberry32(0x510e)
    }
    this.live = this._population()
    this.dragging = false
  }

  /** Where bead `i` lies when the field is down, in screen coordinates. */
  _errSlot(i, n) {
    const o = this.o
    const f = n > 1 ? i / (n - 1) : 0.5
    return [o.width / 2 + (f - 0.5) * o.width * ERR_SPAN, o.height / 2 + o.height * ERR_DROP]
  }

  /** Pull one bead out of the form the cycle drew and put it where the error state wants it. */
  _errPlace(slot, i, n) {
    const e = this.err
    const [lx, ly] = this._errSlot(i, n)

    // A bead at rest on the line is spent material and wears no halo; the halo comes back with
    // the movement, whether the bead stirred on its own or the reader lifted it.
    if ('line' === e.state) {
      const lift = this._errBob(i) + this._errWave(i, n)
      slot.x = lx
      slot.y = ly - lift
      // as in the fall: the halo tracks how alive the bead is, so a slight stir barely glows
      slot.glow = Math.min(1, lift / ERR_BOB_RISE)
      slot.z = 0
      if (!errOnEdge(i)) slot.birth = 0
      return
    }

    if ('lift' === e.state) {
      const k = easeOutCubic(Math.min(1, e.t / ERR_LIFT))
      const fx = e.fx[i]
      const fy = e.fy[i]
      slot.x = fx + (slot.x - fx) * k
      slot.y = fy + (slot.y - fy) * k
      // the field behind the edge arrives as the plane turns out of it
      if (!errOnEdge(i)) slot.birth *= k
      return
    }

    if ('fall' === e.state) {
      const rx = e.fx[i]
      const ry = e.fy[i]
      const glide = Math.max(0.28, Math.min(0.85, e.dur[i] * 0.75))
      const k = easeOutCubic(Math.min(1, e.t / glide))
      const above = errBounce(Math.max(0, ly - ry), e.t)
      const alive = Math.min(1, above / ERR_BOB_RISE)
      slot.x = rx + (lx - rx) * k
      slot.y = ly - above
      // each bead's halo dies with its own motion rather than the whole line losing it at once
      slot.glow = alive
      slot.z = 0
      // and the field sinks back into the plane, leaving its edge behind
      if (!errOnEdge(i)) slot.birth *= alive
    }
  }

  /** The edge bead nearest a point along the line, given as a fraction of its length. */
  _errPick(at) {
    const n = this.o.beads
    const want = Math.min(1, Math.max(0, at)) * (n - 1)
    const snapped = Math.round(want / ERR_EDGE_EVERY) * ERR_EDGE_EVERY
    return Math.min(n - 1 - ((n - 1) % ERR_EDGE_EVERY), Math.max(0, snapped))
  }

  /**
   * The crest the pointer carries: a raised cosine centred on it, so the bead under the cursor
   * lifts most and its neighbours less, out to the edge of its reach.
   */
  _errWave(i, n) {
    if (this.hoverK < 0.001) return 0
    const f = n > 1 ? i / (n - 1) : 0.5
    const near = Math.abs(f - this.hoverX) / ERR_WAVE_REACH
    if (near >= 1) return 0
    return ERR_WAVE_RISE * this.hoverK * 0.5 * (1 + Math.cos(Math.PI * near))
  }

  /** How far bead `i` has stirred out of the line, if it is one of the few currently stirring. */
  _errBob(i) {
    const bobs = this.err.bobs
    let lift = 0
    // summed, not the first match: two stirs can land on one bead, and returning either alone
    // makes it jump to the other the moment that one is culled
    for (let k = 0; k < bobs.length; k++) {
      const bob = bobs[k]
      if (bob.i === i) lift += Math.sin(Math.PI * (bob.t / bob.len)) * bob.rise
    }
    return lift
  }

  /**
   * A bead or two stirring every couple of seconds: the line is not dead material, it is waiting
   * to be picked up. The gap and the stir length together keep it to a handful at a time, so it
   * reads as breathing rather than as motion.
   */
  _stepBobs(dt) {
    const e = this.err
    e.nextBob -= dt
    if (e.nextBob <= 0) {
      const near = this.hoverAt
      const sensed = null !== near
      e.nextBob = ERR_BOB_GAP * (sensed ? ERR_HOVER_GAP : 1) * (0.6 + e.rng() * 0.8)
      const many = e.rng() < (sensed ? 0.7 : 0.35) ? 2 : 1
      for (let k = 0; k < many; k++) {
        const scale =
          (ERR_BOB_LOW + Math.pow(e.rng(), 2) * (ERR_BOB_HIGH - ERR_BOB_LOW)) * (sensed ? ERR_HOVER_RISE : 1)
        e.bobs.push({
          i: this._errPick(sensed ? near + (e.rng() - 0.5) * 2 * ERR_HOVER_REACH : e.rng()),
          t: 0,
          rise: ERR_BOB_RISE * scale,
          // a taller stir lingers, the way a higher throw does -- unless the cursor put it there
          len: ERR_BOB_LEN * Math.sqrt(scale) * (sensed ? ERR_HOVER_QUICK : 1)
        })
      }
    }
    for (let k = e.bobs.length - 1; k >= 0; k--) {
      e.bobs[k].t += dt
      if (e.bobs[k].t >= e.bobs[k].len) e.bobs.splice(k, 1)
    }
  }

  /**
   * Note where every bead is on screen right now. Both the rise and the fall start from here, so
   * a gesture that interrupts either one carries on from what the reader can see rather than from
   * where the field would have been.
   */
  _errCapture() {
    const e = this.err
    const n = this.o.beads
    if (!e.fx || e.fx.length < n) {
      e.fx = new Float32Array(n)
      e.fy = new Float32Array(n)
      e.dur = new Float32Array(n)
    }
    for (let i = 0; i < n; i++) {
      const [lx, ly] = this._errSlot(i, n)
      e.fx[i] = lx
      e.fy[i] = ly
    }
    for (let q = 0; q < this.buf.length; q++) {
      const slot = this.buf[q]
      e.fx[slot.i] = slot.x
      e.fy[slot.i] = slot.y
    }
  }

  /** Take the shape out of the reader's hands: note where every bead was, and drop it. */
  _errRelease() {
    const e = this.err
    const n = this.o.beads
    if (!e || ('held' !== e.state && 'lift' !== e.state)) return
    this._errCapture()
    let longest = 0
    for (let i = 0; i < n; i++) {
      const [, ly] = this._errSlot(i, n)
      e.dur[i] = errSettle(Math.max(0, ly - e.fy[i]))
      if (e.dur[i] > longest) longest = e.dur[i]
    }
    e.state = 'fall'
    e.t = 0
    e.span = longest + 0.1
  }

  /** The reader has taken hold of the line: raise a form out of it. */
  _errLift() {
    const e = this.err
    if (!e || ('line' !== e.state && 'fall' !== e.state)) return
    // only forms the field actually built: `onlyForm` leaves the rest of the row empty, and a
    // hole here reaches the renderer as a missing arrangement. Drawn from the seeded stream, so
    // the same report replays the same way.
    const built = []
    for (let k = 0; k < this.forms.length; k++) if (this.forms[k]) built.push(k)
    if (!built.length) return
    let next = built[0]
    if (built.length > 1) {
      next = this.toIdx
      while (next === this.toIdx) next = built[Math.floor(e.rng() * built.length)]
    }
    this._errCapture()
    this.fromIdx = next
    this.toIdx = next
    this.phase = 'hold'
    this.clock = 0
    e.state = 'lift'
    e.t = 0
  }

  _step(dt) {
    const o = this.o
    const target = this._population()
    if (this.err) {
      this.live = target
      if (this.paletteMix < 1) this.paletteMix = Math.min(1, this.paletteMix + dt / PALETTE_FADE)
      this._stepError(dt)
      return
    }
    if (this.paletteMix < 1) this.paletteMix = Math.min(1, this.paletteMix + dt / PALETTE_FADE)
    if (this.live < Math.min(o.openingPopulation, target)) {
      this.live = Math.min(target, this.live + (o.openingPopulation / o.openingWindow) * dt)
    } else if (this.live < target) {
      this.live = Math.min(target, this.live + o.arrivalRate * dt)
    }

    if (o.reducedMotion) return

    this.clock += dt
    if (this.phase === 'hold') {
      if (this.clock >= o.hold) {
        this.clock -= o.hold
        this.phase = 'travel'
      }
    } else if (this.clock >= o.collapse + o.expand) {
      this.clock -= o.collapse + o.expand
      this.phase = 'hold'
      this.prevIdx = this.fromIdx
      this.fromIdx = this.toIdx
      this.toIdx = this._nextForm()
    }
  }

  _stepError(dt) {
    const e = this.err
    // eased, so the crest arrives and leaves with the pointer rather than snapping on and off
    const want = null === this.hoverAt ? 0 : 1
    this.hoverK += (want - this.hoverK) * Math.min(1, dt * ERR_WAVE_EASE)
    // A shape lives only while it is being held. Let go part-way up -- or merely click -- and it
    // falls from wherever it had got to, so a click cannot leave a form standing on the page.
    if (!this.dragging && ('lift' === e.state || 'held' === e.state)) {
      this._errRelease()
      return
    }
    e.t += dt
    if ('lift' === e.state && e.t >= ERR_LIFT) {
      e.state = 'held'
      e.t = 0
    } else if ('fall' === e.state && e.t >= e.span) {
      e.state = 'line'
      e.t = 0
    }

    // the stir is ambient, so it is the one part of this a reader can decline
    if ('line' === e.state && !this.o.reducedMotion) this._stepBobs(dt)
    else if (e.bobs.length) e.bobs.length = 0
  }

  _hintPose(t) {
    let alpha = 1
    let grab = 0
    let x = -HINT_X
    let y = 0
    let dydx = 0
    const tA = HINT_IN + HINT_GRAB
    const tB = tA + HINT_A
    const tR = tB + HINT_B
    if (t < HINT_IN) alpha = t / HINT_IN
    else if (t < tA) grab = (t - HINT_IN) / HINT_GRAB
    else if (t < tB) {
      grab = 1
      const u = easeInOutSine((t - tA) / HINT_A)
      x = -HINT_X + 2 * HINT_X * u
      y = -Math.sin(Math.PI * u) * HINT_ARC_OUT
      dydx = (-Math.cos(Math.PI * u) * Math.PI * HINT_ARC_OUT) / (2 * HINT_X)
    } else if (t < tR) {
      grab = 1
      const u = easeInOutSine((t - tB) / HINT_B)
      x = HINT_X - 2 * HINT_X * u
      y = Math.sin(Math.PI * u) * HINT_ARC_BACK
      dydx = (Math.cos(Math.PI * u) * Math.PI * HINT_ARC_BACK) / (2 * HINT_X)
    } else if (t < HINT_LEN - HINT_OUT) grab = 1 - (t - tR) / HINT_REL
    else alpha = 1 - (t - (HINT_LEN - HINT_OUT)) / HINT_OUT
    return { x, y, grab, alpha, tilt: Math.atan(dydx) * 0.85 }
  }

  _stepView(dt) {
    if (this.dragging) return
    const o = this.o

    if (!o.reducedMotion && !this.hasDragged && !this.err && this.live >= o.openingPopulation) {
      // Arm once per cycle. Re-arming whenever the countdown is negative resets
      // it every frame, and it never reaches zero.
      if (this.hintPending) {
        this.hintT = this.hintShown === 0 ? -o.hintDelay : -o.hintGap
        this.hintPending = false
      }
      this.hintT += dt
      const tA = HINT_IN + HINT_GRAB
      if (this.hintT >= 0 && this.hintT <= HINT_LEN) {
        if (this.hintT >= tA && this.hintT <= tA + HINT_A + HINT_B) {
          const pose = this._hintPose(this.hintT)
          if (this.hintT - dt < tA) {
            this.hintYaw0 = this.yaw
            this.hintPitch0 = this.pitch
            this.hintX0 = pose.x
            this.hintY0 = pose.y
          }
          this.yaw = this.hintYaw0 + (pose.x - this.hintX0) * 0.004
          this.pitch = Math.max(-1.2, Math.min(1.2, this.hintPitch0 + (pose.y - this.hintY0) * 0.0065))
          return
        }
      } else if (this.hintT > HINT_LEN) {
        this.hintShown++
        this.hintPending = true
      }
    }

    if (o.reducedMotion) return

    this.yaw += this.yawV * dt
    this.pitch += this.pitchV * dt
    const decay = Math.pow(0.12, dt)
    this.yawV *= decay
    this.pitchV *= decay
    if (Math.abs(this.yawV) < o.idleSpin) this.yaw += o.idleSpin * dt
    this.pitch += (o.restPitch - this.pitch) * Math.min(1, dt * 0.35)
  }

  // ---- drawing -------------------------------------------------------------

  _colourIndex(fi, i, meta, len) {
    // eslint-disable-next-line unicorn/prefer-math-trunc -- int32 coercion of a Float32Array read, in a per-bead path
    if (FORMS[fi].tint > 0.5) return (((meta[i * 2 + 1] | 0) % len) + len) % len
    return this.forms[fi].band[i] % len
  }

  // Written into a scratch triple: this runs once or twice per bead per frame.
  _colour(fi, i, meta, out) {
    const c = this.palette[this._colourIndex(fi, i, meta, this.palette.length)]
    const old = this.paletteOld
    if (!old || this.paletteMix >= 1) {
      out[0] = c[0]
      out[1] = c[1]
      out[2] = c[2]
      return out
    }
    const p = old[this._colourIndex(fi, i, meta, old.length)]
    const t = this.paletteMix
    out[0] = p[0] + (c[0] - p[0]) * t
    out[1] = p[1] + (c[1] - p[1]) * t
    out[2] = p[2] + (c[2] - p[2]) * t
    return out
  }

  render(now) {
    const o = this.o
    const g = this.ctx
    const n = o.beads
    const FA = this.forms[this.fromIdx]
    const FB = this.forms[this.toIdx]
    const A = FA.p
    const Am = FA.m
    const B = FB.p
    const Bm = FB.m
    const motA = MOTION[FORMS[this.fromIdx].key]
    const motB = MOTION[FORMS[this.toIdx].key]
    const travelling = this.phase === 'travel'
    const beadK = this._beadScale()
    const cy2 = Math.cos(this.yaw)
    const sy2 = Math.sin(this.yaw)
    const cp = Math.cos(this.pitch)
    const sp = Math.sin(this.pitch)
    const cx0 = o.width / 2
    const cy0 = o.height / 2
    const split = o.collapse / (o.collapse + o.expand)
    const KA = FA.k * o.spacing
    const KB = FB.k * o.spacing
    let zMin = 1e9
    let zMax = -1e9
    let m = 0

    g.clearRect(0, 0, o.width, o.height)

    for (let i = 0; i < n; i++) {
      const rk = this.bead.rank[i]
      if (rk >= this.live) continue
      let bs = 1
      let bp = 1
      const bv = (this.live - rk) / 14
      if (bv < 1) {
        const bc = bv < 0 ? 0 : bv
        const pp = bc - 1
        bs = 1 + 2.4 * pp * pp * pp + 1.4 * pp * pp
        if (bs < 0) bs = 0
        bp = easeOutCubic(bc)
      }

      let ax = A[i * 3]
      let ay = A[i * 3 + 1]
      let az = A[i * 3 + 2]
      if (motA) {
        motA(this.mo, ax, ay, az, Am[i * 2], Am[i * 2 + 1], now)
        ax += this.mo[0]
        ay += this.mo[1]
        az += this.mo[2]
      }
      ax = (ax - FA.cx) * KA
      ay = (ay - FA.cy) * KA
      az = (az - FA.cz) * KA

      let x
      let y
      let z
      let gp = 0
      let squeeze = 1
      if (!travelling) {
        x = ax
        y = ay
        z = az
      } else {
        const st = this.bead.stagger[i]
        gp = clamp01((this.clock / (o.collapse + o.expand) - st) / (1 - st))
        const near = 1 - Math.abs(gp - split) / (gp < split ? split || 1 : 1 - split || 1)
        squeeze = 1 - 0.55 * (near < 0 ? 0 : near)
        let bx = B[i * 3]
        let by = B[i * 3 + 1]
        let bz = B[i * 3 + 2]
        if (motB) {
          motB(this.mo, bx, by, bz, Bm[i * 2], Bm[i * 2 + 1], now)
          bx += this.mo[0]
          by += this.mo[1]
          bz += this.mo[2]
        }
        bx = (bx - FB.cx) * KB
        by = (by - FB.cy) * KB
        bz = (bz - FB.cz) * KB
        const kx = this.bead.cx[i] * o.core
        const ky = this.bead.cy[i] * o.core
        const kz = this.bead.cz[i] * o.core
        if (gp < split) {
          const t1 = easeInCubic(split === 0 ? 1 : gp / split)
          x = ax + (kx - ax) * t1
          y = ay + (ky - ay) * t1
          z = az + (kz - az) * t1
        } else {
          const t2 = easeOutCubic(split === 1 ? 1 : (gp - split) / (1 - split))
          x = kx + (bx - kx) * t2
          y = ky + (by - ky) * t2
          z = kz + (bz - kz) * t2
        }
      }

      if (bp < 1) {
        x *= bp
        y *= bp
        z *= bp
      }

      // Yaw then pitch. These signs make the near face follow the pointer; the
      // opposite pair turns the object away from the drag.
      const rx = x * cy2 - z * sy2
      const rz0 = x * sy2 + z * cy2
      const ry = y * cp - rz0 * sp
      const rz = y * sp + rz0 * cp
      const sc = o.focal / (o.focal + rz + 260)

      const cA = this._colour(this.fromIdx, i, Am, this._cA)
      let cr = cA[0]
      let cg = cA[1]
      let cb = cA[2]
      if (travelling) {
        const cB = this._colour(this.toIdx, i, Bm, this._cB)
        cr += (cB[0] - cr) * gp
        cg += (cB[1] - cg) * gp
        cb += (cB[2] - cb) * gp
      }

      let slot = this.buf[m]
      if (!slot) {
        slot = { x: 0, y: 0, r: 0, z: 0, i: 0, birth: 1, cr: 0, cg: 0, cb: 0, R: 0, glow: 1 }
        this.buf[m] = slot
      }
      slot.i = i
      slot.birth = bs * squeeze
      slot.z = rz
      slot.x = cx0 + rx * sc
      slot.y = cy0 + ry * sc
      slot.glow = 1
      if (this.err) this._errPlace(slot, i, n)
      slot.r = sc
      slot.cr = cr
      slot.cg = cg
      slot.cb = cb
      if (rz < zMin) zMin = rz
      if (rz > zMax) zMax = rz
      m++
    }

    if (m === 0) return
    this.buf.length = m

    // Painter's order by counting sort: a comparator sort of a thousand objects
    // every frame is thousands of calls for bucket-resolution ordering.
    const zSpan = zMax - zMin || 1
    if (!this.binOf || this.binOf.length < m) {
      this.binOf = new Int32Array(m)
      this.sorted = Array.from({ length: m })
    }
    this.bins.fill(0)
    for (let i = 0; i < m; i++) {
      // eslint-disable-next-line unicorn/prefer-math-trunc -- bucket index, once per bead per frame
      const bq = (((this.buf[i].z - zMin) / zSpan) * (BINS - 1)) | 0
      this.binOf[i] = bq
      this.bins[bq]++
    }
    let acc = 0
    for (let q = BINS - 1; q >= 0; q--) {
      const c = this.bins[q]
      this.bins[q] = acc
      acc += c
    }
    for (let i = 0; i < m; i++) this.sorted[this.bins[this.binOf[i]]++] = this.buf[i]

    // Radii first, then one uniform zoom: fitting in model space cannot promise
    // the frame, because spacing, perspective, halo and drag all change the
    // projected extent afterwards.
    const half = o.size * 0.5
    let extX = 1
    let extY = 1
    for (let q = 0; q < m; q++) {
      const s = this.sorted[q]
      const t = (s.z - zMin) / zSpan
      s.R = half * beadK * this.bead.size[s.i] * s.r * (1 + (0.5 - t) * o.depthFalloff) * s.birth
      const re = s.R * (1 + o.outline)
      const dx = Math.abs(s.x - cx0) + re
      const dy = Math.abs(s.y - cy0) + re
      if (dx > extX) extX = dx
      if (dy > extY) extY = dy
    }
    const fitK = Math.min(1, (o.width / 2 - 4) / extX, (o.height / 2 - 4) / extY)

    for (let q = 0; q < m; q++) {
      const s = this.sorted[q]
      const R = s.R * fitK
      if (R < 0.4) continue
      const px = cx0 + (s.x - cx0) * fitK
      const py = cy0 + (s.y - cy0) * fitK
      /* eslint-disable unicorn/prefer-math-trunc -- channel coercion, once per bead per frame */
      const cr = s.cr | 0
      const cg = s.cg | 0
      const cb = s.cb | 0
      /* eslint-enable unicorn/prefer-math-trunc */
      g.beginPath()
      g.arc(px, py, R, 0, TAU)
      g.fillStyle = `rgb(${cr},${cg},${cb})`
      g.fill()
      if (o.outline > 0.01 && R > 1.6 && s.glow > 0.02) {
        g.lineWidth = Math.max(0.7, R * 2 * o.outline)
        g.strokeStyle = `rgba(${cr},${cg},${cb},${1 === s.glow ? o.outlineAlpha : o.outlineAlpha * s.glow})`
        g.stroke()
      }
    }

    this._drawHint(g)
  }

  _drawHint(g) {
    // The error screen is asking the reader to press Reload; a hand teaching a toy would compete
    // with it, and the line's own stirring already says it can be touched.
    if (this.o.reducedMotion || this.hasDragged || this.err) return
    if (this.hintT < 0 || this.hintT > HINT_LEN) return
    const pose = this._hintPose(this.hintT)
    const x = this.o.width / 2 + pose.x
    const y = this.o.height - 62 + pose.y
    const scale = 1.15 * (1 - pose.grab * 0.1)

    g.save()
    g.globalAlpha = pose.alpha
    g.beginPath()
    g.ellipse(x, y + 2, 32, 25, 0, 0, TAU)
    g.fillStyle = 'rgba(255,255,255,0.82)'
    g.fill()
    g.translate(x, y)
    g.rotate(pose.tilt)
    g.scale(scale, scale)
    g.translate(-12, -13)
    g.lineCap = 'round'
    g.lineJoin = 'round'
    for (let pass = 0; pass < 2; pass++) {
      g.strokeStyle = pass ? 'rgba(34,61,100,0.95)' : 'rgba(255,255,255,0.95)'
      g.lineWidth = pass ? 1.9 : 4.4
      for (let i = 0; i < this.hand.length; i++) g.stroke(this.hand[i])
    }
    g.restore()
  }

  // ---- loop ----------------------------------------------------------------

  start() {
    if (this.raf) return
    // stop() unbinds the pointer, and callers use the pair as pause/resume -- without this a field
    // that has been scrolled past once can never be turned again
    this._bindPointer()
    const frame = ts => {
      this.raf = window.requestAnimationFrame(frame)
      if (!this.last) this.last = ts
      const dt = Math.min(0.05, (ts - this.last) / 1000)
      this.last = ts
      try {
        this._step(dt)
        this._stepView(dt)
        this.render(ts / 1000)
      } catch (error) {
        // The loop re-arms before it draws, so an error here would otherwise repeat every frame
        // for the life of the report. Decoration is not worth that: stand down instead.
        this.stop()
        if (window.console) window.console.info('stats preloader: stopped after a drawing error', error)
      }
    }
    this.raf = window.requestAnimationFrame(frame)
  }

  stop() {
    if (this.raf) window.cancelAnimationFrame(this.raf)
    this.raf = null
    this._unbindPointer()
  }
}
